import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Chart } from 'chart.js/auto';
import { DashboardParkingStatisticModel } from '../../models';

@Component({
  selector: 'aggregator-dashboard-line-chart',
  templateUrl: './line-chart.component.html',
  styleUrl: './line-chart.component.scss',
  standalone: true,
  imports: [],
})
export class DashboardLineChartComponent implements OnChanges, OnInit, OnDestroy {
  @Input() data?: DashboardParkingStatisticModel;
  @ViewChild('lineChartRef', { static: true })
  chartCanvas!: ElementRef<HTMLCanvasElement>;

  chart!: Chart;

  ngOnInit(): void {
    this.createChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.createChart();
    }
  }

  createChart(): void {
    // Destroy the chart if it exists
    if (this.chart) {
      this.chart.destroy();
    }

    // Get the 2D context of the canvas
    const ctx = this.chartCanvas.nativeElement.getContext('2d');
    if (!ctx) {
      return;
    }

    const hourlyData = this.data?.hourlyData ?? [];
    const maxValue =
      hourlyData.length > 0
        ? Math.max(...hourlyData.flatMap(item => [item.averageOccupied ?? 0]))
        : 0;
    // Calculate yMax as the smallest multiple of 9 that is greater than maxValue
    // This ensures exactly 10 ticks (0 to yMax with 9 intervals) with integer step
    const yMax = maxValue < 0 ? 9 : Math.ceil((maxValue + 1) / 9) * 9;
    const yStep = yMax / 9;

    // Create a new Chart instance
    this.chart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: this.data?.hourlyData?.map(item => item.hour + ':00') ?? [],
        datasets: [
          {
            label: 'Средняя загруженность',
            data: this.data?.hourlyData?.map(item => item.averageOccupied ?? 0) ?? [],
            backgroundColor: '#4caf50',
            borderColor: '#4caf50',
            tension: 0.4,
            fill: false,
            clip: false, // Allow line to be drawn over the axis without clipping
          },
        ],
      },
      options: {
        interaction: {
          mode: 'index',
          intersect: false,
        },
        plugins: {
          legend: {
            display: false, // Hide the legend
          },
          tooltip: {
            callbacks: {
              title: items => {
                const dataIndex = items[0]?.dataIndex;
                const point = hourlyData[dataIndex];
                return point != null ? `Час: ${point.hour}:00` : '';
              },
              label: () => '', // скрываем стандартную строку, все поля показываем в afterBody
              afterBody: items => {
                const dataIndex = items[0]?.dataIndex;
                if (dataIndex == null || !hourlyData[dataIndex]) return [];
                const p = hourlyData[dataIndex];
                return [
                  `Средняя загруженность: ${p.averageOccupied ?? 0}`,
                  `Макс. загруженность: ${p.maxOccupied ?? 0}`,
                  `Мин. загруженность: ${p.minOccupied ?? 0}`,
                  `Коэффициент заполнения: ${p.occupancyRate ?? 0}%`,
                ];
              },
            },
          },
        },
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            min: 0,
            max: yMax,
            grid: {
              z: 0, // Draw grid lines under datasets so line is always visible over axis
            },
            ticks: {
              stepSize: yStep,
              callback: value => (Number.isInteger(value) ? value : ''),
            },
          },
          x: {
            grid: {
              display: false, // Hide vertical grid lines
            },
          },
        },
      },
    });
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
  }
}
